// src/pages/epl/innovatech/LabStepper.jsx
// 🚀 EduTechPulse Lab - Navegación limpia sin dependencias legacy + EPL UX/UI Policy
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
// 🎨 Componentes EPL UX/UI Policy
import EPLSectionHeader from '../../../components/EPLSectionHeader';
import EPLProgressStepper from '../../../components/EPLProgressStepper';
import { 
  ArrowLeft, 
  ArrowRight, 
  Home,
  CheckCircle,
  Circle,
  Users,
  User,
  Clock,
  Target,
  Lightbulb,
  TrendingUp,
  Compass,
  MessageSquare,
  Wrench,
  BarChart3,
  Beaker,
  GraduationCap,
  ClipboardCheck,
  Award
} from 'lucide-react';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
  ? _eplConsole
  : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

// � Helper para íconos por fase según EPL Policy
const getPhaseIcon = (phaseKey) => {
  const icons = {
    contexto: Compass,
    hipotesis: Lightbulb,
    bml_build: Wrench,
    bml_measure: BarChart3,
    bml_simulate: Beaker,
    bml_learn: GraduationCap,
    quiz: ClipboardCheck,
    resumen: Award
  };
  return icons[phaseKey] || Compass;
};

// 🆑 COMPONENTES LIMPIOS - Sistema EduTechPulse Lab nativo
import InnovaTechCard from '../../../components/InnovaTechCard';
import AIResponseCard from '../../../components/AIResponseCard';
import BuildMeasureLearnCircuit from '../../../components/BuildMeasureLearnCircuit';
import BMLBuildPhase from '../../../components/BMLBuildPhase';
import BMLMeasurePhase from '../../../components/BMLMeasurePhase';
import BMLSimulatePhase from '../../../components/BMLSimulatePhase';
import BMLLearnPhase from '../../../components/BMLLearnPhase';
import LeanStartupQuiz from '../../../components/LeanStartupQuiz';

// 🎯 COMPONENTES MODULARES - Nueva arquitectura de steps
import {
  ContextoStep,
  HipotesisStep,
  BMLBuildStep,
  BMLMeasureStep,
  BMLSimulateStep,
  BMLLearnStep,
  QuizStep,
  ResumenStep
} from './components/steps';

// � SERVICIOS MONGODB
import innovatechService from '../../../services/innovatechService';
// �🔐 HOOK DE GATING PEDAGÓGICO
import useInnovaTechGating from '../../../hooks/useInnovaTechGating';
// 🔄 HOOK DE AUTOSAVE PEDAGÓGICO
// import useLabAutosave from '../../../hooks/useLabAutosave'; // ❌ DESHABILITADO: Para QA limpio
// 🎯 HOOK DE AUTOSAVE INTELIGENTE - Solo por eventos
// import useSmartAutosave from '../../../hooks/useSmartAutosave'; // ❌ DESHABILITADO: Causando loop infinito
// 💾 HOOK DE PERSISTENCIA COMPLETA
import useLabPersistence from '../../../hooks/useLabPersistence';
// 🧪 MOCK API para InnovaTech según lineamiento v1.0
import { mockSessionAdvance, AI_CONFIG } from '../../../services/innovatechMockAPI';

// 🎯 Función para generar evaluación CRÍTICA desde contenido IA real
function generateEvaluationFromContent(content) {
  // 🔍 Handle both JSON objects and strings
  const contentStr = typeof content === 'string' 
    ? content 
    : JSON.stringify(content);
  const contentLower = contentStr.toLowerCase();
  let baseScore = 60; // Score base más conservador
  
  // 🚨 DETECTAR CASOS INVIABLES - Score bajo automático
  const inviableCases = ['astronaut', 'marte', 'luna', 'espacio', 'alien', 'dinosaur', 'imposible', 'fantasi', 'irreal'];
  const hasInviableCase = inviableCases.some(word => contentLower.includes(word));
  
  if (hasInviableCase) {
    baseScore = 35; // Score muy bajo para casos irreales
  } else {
    // Criterios positivos solo si es viable
    if (contentLower.includes('problema') && contentLower.includes('específico')) baseScore += 8;
    if (contentLower.includes('cliente') && contentLower.includes('accesibl')) baseScore += 6;
    if (contentLower.includes('mercado') && contentLower.includes('real')) baseScore += 5;
    if (contentLower.includes('observa') && contentLower.includes('verifica')) baseScore += 4;
  }
  
  // Generar variación en criterios individuales
  return {
    score_claridad: Math.min(100, baseScore + Math.floor(Math.random() * 10)),
    score_viabilidad: Math.min(100, baseScore + Math.floor(Math.random() * 8)),
    score_innovacion: Math.min(100, baseScore + Math.floor(Math.random() * 12)),
    score_impacto: Math.min(100, baseScore + Math.floor(Math.random() * 6)),
    score_total: Math.min(100, baseScore + Math.floor(Math.random() * 8))
  };
}

const LabStepper = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🆕 Estado para sistema IA limpio - EduTechPulse Lab nativo
  const [aiResponse, setAiResponse] = useState(null);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  
  // 🎯 Estados para el flujo completo Lean Startup
  const [userHypothesis, setUserHypothesis] = useState('');
  const [bmlCircuitData, setBmlCircuitData] = useState(null);
  const [bmlBuildData, setBmlBuildData] = useState(null);
  const [bmlMeasureData, setBmlMeasureData] = useState(null);
  const [bmlSimulateData, setBmlSimulateData] = useState(null);
  const [bmlLearnData, setBmlLearnData] = useState(null);
  const [quizResults, setQuizResults] = useState(null);
  const [labProgress, setLabProgress] = useState({
    contexto: true, // Siempre completado al estar
    hipotesis: false,
    bml_build: false,
    bml_measure: false,
    bml_learn: false,
    quiz: false,
    resumen: false
  });
  const [showAutosaveIndicator, setShowAutosaveIndicator] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // 🔐 HOOK DE GATING PEDAGÓGICO - REACTIVADO para consolidación
  const gating = useInnovaTechGating(labId, currentStep, sessionData);

    // 🎯 HOOK DE AUTOSAVE INTELIGENTE - Solo por eventos del usuario
  // const smartAutosave = useSmartAutosave(sessionData?.session_id, labId); // ❌ DESHABILITADO: Causando loop infinito

  // 💾 HOOK DE PERSISTENCIA COMPLETA (usar clave fija para Lab 1)
  const labPersistence = useLabPersistence('lab_1');

  // � Helper function para mapear step index a persistence key
  const getStepKey = (stepIndex) => {
    const keyMap = {
      0: 'contexto',      // Contexto
      1: 'hipotesis',     // Hipótesis  
      2: 'bml_build',     // BUILD
      3: 'bml_measure',   // MEASURE
      4: 'bml_simulate',  // SIMULATE
      5: 'bml_learn',     // LEARN
      6: 'quiz',          // Quiz
      7: 'resumen'        // Resumen
    };
    return keyMap[stepIndex] || 'contexto';
  };

  // �📋 Configuración de laboratorios - Lean Startup Bootcamp (memorizada)
  const labsConfig = useMemo(() => ({
    lab1: {
      id: 'lab1',
      title: 'Lean Startup Bootcamp',
      mode: 'individual',
      description: 'Mapear hipótesis central y circuito Build–Measure–Learn',
      objective: 'Objetivo UX: mapear hipótesis central y circuito Build–Measure–Learn',
      steps: [
        {
          id: 'contexto',
          title: 'Contexto',
          type: 'narrativa',
          description: 'Bienvenido al Bootcamp Lean Startup'
        },
        {
          id: 'hipotesis_central',
          title: 'Hipótesis Central',
          type: 'formulario_innovatech',
          description: 'Define tu hipótesis central usando el formato sugerido'
        },
        {
          id: 'bml_build',
          title: 'BUILD: Construye tu MVP',
          type: 'bml_phase',
          description: 'Define qué construir para validar tu hipótesis'
        },
        {
          id: 'bml_measure',
          title: 'MEASURE: Mide el resultado',
          type: 'bml_phase',
          description: 'Define cómo vas a medir el éxito'
        },
        {
          id: 'bml_simulate',
          title: 'SIMULATE: Ejecutar experimento',
          type: 'bml_phase',
          description: 'Genera datos simulados de tu experimento'
        },
        {
          id: 'bml_learn',
          title: 'LEARN: Aprende y decide',
          type: 'bml_phase',
          description: 'Analiza resultados y decide próximos pasos'
        },
        {
          id: 'quiz_comprension',
          title: 'Quiz de Comprensión',
          type: 'evaluacion',
          description: 'Valida tu comprensión del proceso'
        },
        {
          id: 'resumen',
          title: 'Resumen + Próximos Pasos',
          type: 'conclusion',
          description: 'Revisa tu progreso y planifica siguientes acciones'
        }
      ]
    }
  }), []);

  const currentLab = useMemo(() => labsConfig[labId], [labsConfig, labId]);

  // � Check de autenticación
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.warn('❌ Usuario no autenticado. Redirigiendo...');
      navigate('/login');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  // � EduTechPulse Lab - Carga de sesión simplificada
  useEffect(() => {
    const loadSession = async () => {
      // Evitar múltiples llamadas simultáneas
      if (isCreatingSession || sessionData) {
        return;
      }
      
      setLoading(true);
      setIsCreatingSession(true);
      
      // Validar que el lab existe
      if (!labsConfig[labId]) {
        setLoading(false);
        setIsCreatingSession(false);
        return;
      }
      
      const lab = labsConfig[labId];
      
      try {
        // Crear sesión con ID único mejorado (evita race conditions)
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const sessionId = `lab_session_${timestamp}_${randomSuffix}`;
        const createdSession = await innovatechService.createSession({
          sessionId: sessionId,
          labId: labId,
          stepId: lab.steps[currentStep].id,
          mode: lab.mode || 'individual'
        });
        
        console.log('🚀🟢 [SESIÓN CREADA] MongoDB:', sessionId);
        console.log('🔍📋 [DEBUG] Respuesta completa de createSession:', createdSession);
        setSessionData(createdSession.session || createdSession);
        
      } catch (error) {
        console.error('[❌ MongoDB] Error creando sesión:', error);
        
        // Fallback: sesión mock genérica
        const mockSession = {
          session_id: `lab_session_fallback_${Date.now()}`,
          user_id: user?._id || user?.id,
          lab_id: labId,
          step_id: lab.steps[currentStep].id,
          mode: lab.mode,
          status: 'active'
        };
        
        setSessionData(mockSession);
      } finally {
        setLoading(false);
        setIsCreatingSession(false);
      }
      
      // 🔄 Autosave temporalmente deshabilitado para debug
      // setTimeout(() => {
      //   if (autosave?.recoverSavedData) {
      //     const recoveredData = autosave.recoverSavedData();
      //     if (recoveredData?.data) {
      //       const { data } = recoveredData;
      //       
      //       // Restaurar estados si existen datos previos
      //       if (data.currentStep !== undefined) setCurrentStep(data.currentStep);
      //       if (data.userHypothesis) setUserHypothesis(data.userHypothesis);
      //       if (data.bmlCircuitData) setBmlCircuitData(data.bmlCircuitData);
      //       if (data.quizResults) setQuizResults(data.quizResults);
      //       if (data.labProgress) setLabProgress(data.labProgress);
      //       if (data.aiResponse) setAiResponse(data.aiResponse);
      //       
      //       // 📊 Restaurar datos de input libre como fuente de verdad
      //       if (data.current_hypothesis) {
      //         setSessionData(prev => ({
      //           ...prev,
      //           current_hypothesis: data.current_hypothesis
      //         }));
      //         console.log('[📊 LabStepper] Hipótesis restaurada desde DB:', data.current_hypothesis.content);
      //       }
      //       
      //       console.log('[🔄 LabStepper] Datos recuperados del autosave:', data);
      //     }
      //   }
      // }, 100); // Pequeño delay para que el hook se inicialice
      
      setLoading(false);
    };

    // Solo cargar sesión si el usuario está autenticado
    if (isAuthenticated && user) {
      loadSession();
    }
  }, [labId, currentStep, isAuthenticated, user]); // ✅ Agregado user e isAuthenticated

  // 💾 Cargar datos persistidos cuando el hook esté listo
  useEffect(() => {
    if (labPersistence.persistedData && !labPersistence.isLoading) {
      const { persistedData } = labPersistence;
      
      console.log('[💾 LabStepper] Cargando datos persistidos:', persistedData);
      console.log('[💾 LabStepper] Estado de persistencia del labProgress:', persistedData.labProgress);
      
      // Restaurar estados de formulario
      if (persistedData.formData.hipotesis) setUserHypothesis(persistedData.formData.hipotesis);
      if (persistedData.formData.bml_build) setBmlBuildData(persistedData.formData.bml_build);
      if (persistedData.formData.bml_measure) setBmlMeasureData(persistedData.formData.bml_measure);
      if (persistedData.formData.bml_learn) setBmlLearnData(persistedData.formData.bml_learn);
      
      // 🔄 Restaurar resultados del quiz desde aiResponse (no formData)
      if (persistedData.aiResponses.quiz) {
        setQuizResults(persistedData.aiResponses.quiz);
        console.log('[💾 LabStepper] Quiz results restaurados:', persistedData.aiResponses.quiz);
      }
      
      // Convertir labProgress persistido al formato actual
      const newLabProgress = {
        contexto: true,
        hipotesis: persistedData.labProgress.hipotesis?.approved || false,
        bml_build: persistedData.labProgress.bml_build?.approved || false,
        bml_measure: persistedData.labProgress.bml_measure?.approved || false,
        bml_learn: persistedData.labProgress.bml_learn?.approved || false,
        quiz: persistedData.labProgress.quiz?.approved || false,
        resumen: persistedData.labProgress.resumen?.approved || false
      };
      
      setLabProgress(newLabProgress);
      
      console.log('[💾 LabStepper] Estados restaurados:', {
        labProgress: newLabProgress,
        hasHypothesis: !!persistedData.formData.hipotesis,
        hasBmlBuild: !!persistedData.formData.bml_build,
        hasBmlMeasure: !!persistedData.formData.bml_measure,
        hasBmlLearn: !!persistedData.formData.bml_learn
      });
      
      // 🔍 Debug adicional para verificar progreso individual
      console.log('[🔍 LabStepper] Progreso individual por fase:');
      Object.keys(persistedData.labProgress).forEach(phase => {
        const phaseData = persistedData.labProgress[phase];
        if (typeof phaseData === 'object' && phaseData !== null) {
          console.log(`  ${phase}: approved=${phaseData.approved}, score=${phaseData.score}`);
        }
      });
    }
  }, [labPersistence.persistedData, labPersistence.isLoading]);

  // 🔍 Debug: Monitorear cambios en labProgress
  useEffect(() => {
    console.log('[🔍 LabProgress] Estado actualizado:', labProgress);
  }, [labProgress]);

  // 💾 MANEJAR PERSISTENCIA DE INPUT LIBRE (según lineamiento)
  const handleInputLibrePersistence = async (inputData) => {
    // console.log('[📊 LabStepper] Persistiendo input libre en MongoDB:', inputData); // 🔇 QA Mode
    
    try {
      // 1. Asegurar que tenemos sesión activa
      const currentSessionId = sessionData?.session_id || innovatechService.getOrCreateSessionId(labId);
      
      // 2. Si no hay sesión en MongoDB, usar la existente (evitar duplicados)
      if (!sessionData?.session_id) {
        console.warn('[⚠️ LabStepper] No hay sesión cargada. Usando fallback local.');
        // Evitar crear sesiones duplicadas - usar loadSession en useEffect en su lugar
        return; // Salir si no hay sesión activa
      }

      // 3. Guardar mensaje en MongoDB (solo si hay contenido suficiente)
      if (inputData.content && inputData.content.length > 10) {
        const messageResponse = await innovatechService.saveMessage({
          sessionId: currentSessionId,
          labId: labId,
          stepId: 'hipotesis_central',
          content: inputData.content,
          eventType: 'input_libre'
        });

        // console.log('[✅ LabStepper] Input persistido en MongoDB:', messageResponse.message._id); // 🔇 QA Mode
      }
      
      setSessionData(prev => ({
        ...prev,
        current_hypothesis: {
          ...inputData,
          session_id: currentSessionId,
          lab_id: labId,
          step_id: 'hipotesis_central',
          updated_at: inputData.client_ts,
          persisted_in_mongodb: true
        }
      }));

      // 🎯 Usar SmartAutosave para persistencia inteligente
      // ❌ DESHABILITADO: Causando loop infinito
      // smartAutosave.saveOnInput({
      //   content: inputData.content,
      //   stepId: 'hipotesis_central',
      //   ...inputData
      // });

      // console.log('[📊 LabStepper] Input libre NO persistido - SmartAutosave deshabilitado'); // 🔇 QA Mode
      
    } catch (error) {
      console.error('[❌ LabStepper] Error persistiendo en MongoDB:', error);
      
      // Verificar si es error de autenticación
      if (error.message?.includes('Token') || error.message?.includes('401') || error.message?.includes('autenticación')) {
        console.error('[🔐 LabStepper] Error de autenticación detectado. Redirigiendo...');
        navigate('/login');
        return;
      }
      
      // Fallback: usar sessionData local temporalmente
      setSessionData(prev => ({
        ...prev,
        current_hypothesis: {
          ...inputData,
          session_id: prev?.session_id,
          lab_id: labId,
          step_id: 'hipotesis_central',
          updated_at: inputData.client_ts,
          fallback: true // Marcar como fallback
        }
      }));
    }
  };

  // 🤖 FUNCIÓN GENÉRICA PARA VALIDACIÓN IA BML
  const processAIFeedback = async (content, stepId, formData = null) => {
    console.log(`[🤖 USER ACTION] Validando ${stepId} con IA`); // ✅ Acción usuario
    
    setIsProcessingAI(true);
    setAiResponse(null);

    try {
      // 💾 Verificar si necesita re-evaluación con IA
      const shouldReEval = labPersistence.shouldReEvaluate(stepId, formData || content);
      
      if (!shouldReEval) {
        // Usar evaluación existente
        const existingState = labPersistence.getStepState(stepId);
        console.log(`[✅ CACHE] Usando evaluación existente para ${stepId}`);
        
        setAiResponse({
          content: existingState.aiResponse.content,
          role: existingState.aiResponse.role,
          source: 'cache',
          cached: true
        });
        
        setIsProcessingAI(false);
        return;
      }
      // � GUARDAR INPUT DEL USUARIO EN MONGODB PRIMERO
      const currentSessionId = sessionData?.session_id || innovatechService.getOrCreateSessionId(labId);
      
      await innovatechService.saveMessage({
        sessionId: currentSessionId,
        labId: labId,
        stepId: stepId,
        content: content,
        eventType: 'input_libre'
      });
      
      console.log(`[✅ MongoDB] Guardado input usuario: ${stepId}`);

      // �🚀 MONGODB + IA: Procesamiento integrado con auditoría completa
      const response = await innovatechService.processWithAI({
        sessionId: currentSessionId,
        labId: labId,
        stepId: stepId,
        content: content,
        aiRole: stepId === 'bml_measure' ? 'analista' : 'tutor' // Analista para MEASURE, tutor para BUILD/LEARN
      });

      // 🎯 CONTRATO JSON ESTÁNDAR v1.0 - Adaptación frontend
      const isNewContract = response?.contract_version === "1.0";
      
      console.log('[🔍 LabStepper] Contrato v1.0 detectado:', isNewContract);
      
      const aiResponse = {
        content: isNewContract ? response?.payload?.output : (response?.content || 'Error: Sin contenido'),
        role: response?.role || 'tutor',
        source: response?.source || 'unknown',
        contract_type: isNewContract ? 'json' : (response?.contract_type || 'text'),
        gating: response?.gating,
        feedback_text: isNewContract 
          ? (response?.payload?.content || response?.payload?.output) 
          : (response?.content || 'Error: Sin feedback')
      };
      
      // ✅ Score desde nuevo contrato con fallback multi-fuente (Fix para role analista)
      let scoreTotal = aiResponse.content?.scores?.total ||        // Formato tutor 
                      aiResponse.content?.gate?.score_total ||     // Formato analista  
                      aiResponse.score ||                          // Backend direct score
                      aiResponse.gating?.last_score;
      
      // 🔧 Validación y normalización en frontend
      const validScore = typeof scoreTotal === 'number' && !Number.isNaN(scoreTotal) && scoreTotal >= 0;
      
      if (!validScore) {
        console.warn(`[⚠️ SCORE] Invalid score received: ${scoreTotal}, setting to null`);
        scoreTotal = null;
      }
      
      const hypothesisApproved = validScore ? scoreTotal >= 70 : false;
      
      const scoreLabel = validScore ? `${scoreTotal}/100` : 'Pendiente de evaluación';
      console.log(`[✅ USER RESULT] Score IA: ${scoreLabel}, ${stepId} ${hypothesisApproved ? 'APROBADA' : 'RECHAZADA'}`);

      // 💾 Guardar datos del formulario según la fase
      if (stepId === 'hipotesis_central') {
        // Actualizar progreso de hipótesis central
        setLabProgress(prev => ({ ...prev, hipotesis: hypothesisApproved }));
        // 💾 Guardar también en persistencia (usar clave correcta 'hipotesis')
        labPersistence.saveStepProgress('hipotesis', {
          approved: hypothesisApproved,
          score: validScore ? scoreTotal : null,
          formData: content, // El contenido de la hipótesis
          aiResponse: aiResponse
        });
      } else if (stepId === 'bml_build' && formData) {
        setBmlBuildData(formData);
        setLabProgress(prev => ({ ...prev, bml_build: hypothesisApproved }));
        // 💾 Guardar también en persistencia
        labPersistence.saveStepProgress('bml_build', {
          approved: hypothesisApproved,
          score: validScore ? scoreTotal : null,
          formData: formData,
          aiResponse: aiResponse
        });
      } else if (stepId === 'bml_measure' && formData) {
        setBmlMeasureData(formData);
        setLabProgress(prev => ({ ...prev, bml_measure: hypothesisApproved }));
        // 💾 Guardar también en persistencia
        labPersistence.saveStepProgress('bml_measure', {
          approved: hypothesisApproved,
          score: validScore ? scoreTotal : null,
          formData: formData,
          aiResponse: aiResponse
        });
      } else if (stepId === 'bml_learn' && formData) {
        setBmlLearnData(formData);
        setLabProgress(prev => ({ ...prev, bml_learn: hypothesisApproved }));
        // 💾 Guardar también en persistencia
        labPersistence.saveStepProgress('bml_learn', {
          approved: hypothesisApproved,
          score: validScore ? scoreTotal : null,
          formData: formData,
          aiResponse: aiResponse
        });
      }

      // � GATING PEDAGÓGICO - Calcular progreso de sesión
      const sessionProgress = {
        completion_percentage: hypothesisApproved ? 80 : 40,
        can_advance: hypothesisApproved,
        min_score_required: 70,
        last_score: validScore ? scoreTotal : null,
        score_label: scoreLabel
      };

      // �📊 Preparar datos de feedback IA REAL
      const feedbackData = {
        feedback_id: `ai_real_${Date.now()}`,
        message_id: `msg_real_${Date.now()}`,
        ai_role: aiResponse.role,
        feedback_text: aiResponse.feedback_text || (typeof aiResponse.content === 'string' 
          ? aiResponse.content 
          : `${aiResponse.content?.fortaleza || ''}\n\n${aiResponse.content?.mejora || ''}\n\n${aiResponse.content?.accion || ''}`),
        evaluation: aiResponse.evaluation || null,
        approved: hypothesisApproved,
        source: aiResponse.source || 'openai',
        session_id: sessionData?.session_id || response.session_id,
        completion_percentage: sessionProgress.completion_percentage,
        timestamp: new Date().toISOString(),
        // 🎯 CRITICAL: Preservar propiedades para JSON rendering y UI correcta
        contract_type: aiResponse.contract_type,
        content: aiResponse.content,
        gating: aiResponse.gating,
        aiSource: aiResponse.source
      };

      // 💾 GUARDAR FEEDBACK DE IA EN MONGODB
      await innovatechService.saveFeedback({
        session_id: sessionData?.session_id,
        lab_id: labId,
        step_id: stepId,
        ai_role: aiResponse.role || 'tutor',
        feedback_text: typeof feedbackData.feedback_text === 'string' 
          ? feedbackData.feedback_text 
          : JSON.stringify(feedbackData.feedback_text),
        evaluation: {
          ...aiResponse.evaluation,
          veredicto: aiResponse.evaluation?.veredicto || (hypothesisApproved ? 'aprobado' : 'revisar'),
          scores: {
            ...aiResponse.evaluation?.scores,
            total: scoreTotal || aiResponse.evaluation?.scores?.total || 0
          }
        },
        ai_model: 'gpt-4o-mini',
        tokens_used: aiResponse.tokens_used || 0
      });
      
      console.log(`🤖💾 [FEEDBACK GUARDADO] IA feedback guardado en MongoDB: ${stepId}`);

      // 💾 Guardar en persistencia local (mapear stepId del backend a clave de persistencia)
      const persistenceKey = stepId === 'hipotesis_central' ? 'hipotesis' : stepId;
      // 🎯 Normalizar aiResponse para asegurar score consistente
      const normalizedAiResponse = {
        ...aiResponse,
        score_total: scoreTotal,  // Asegurar que score_total esté disponible
        score: scoreTotal         // Asegurar que score esté disponible
      };

      labPersistence.saveStepProgress(persistenceKey, {
        approved: hypothesisApproved,
        score: scoreTotal,
        formData: formData || content,
        aiResponse: normalizedAiResponse
      });

      console.log(`[💾 PERSISTENCIA] Guardado progreso local para ${stepId}:`, {
        approved: hypothesisApproved,
        score: scoreTotal,
        formDataExists: !!formData
      });
      
      // 🔍 Debug: Verificar que realmente se guardó
      setTimeout(() => {
        const verification = labPersistence.getStepState(stepId);
        console.log(`[🔍 VERIFICACIÓN] Estado guardado de ${stepId}:`, verification);
      }, 100);

      // Guardar respuesta IA con datos de evaluación
      setAiResponse(feedbackData);

    } catch (error) {
      console.error('[❌ LabStepper] Error procesando IA:', error);
      console.error('[🔍 DEBUG] Detalles del error:', {
        message: error.message,
        name: error.name,
        stack: error.stack?.substring(0, 300),
        isNetworkError: error.name === 'TypeError' && error.message.includes('fetch'),
        userAgent: navigator.userAgent,
        isMobile: /Mobi|Android/i.test(navigator.userAgent),
        currentURL: window.location.href
      });
      
      // Verificar si es error de autenticación
      if (error.message?.includes('Token') || error.message?.includes('401') || error.message?.includes('autenticación')) {
        console.error('[🔐 LabStepper] Error de autenticación detectado. Redirigiendo...');
        navigate('/login');
        return;
      }
      
      // Detectar tipo específico de error para móvil
      let errorMessage = 'Error conectando con IA. Por favor, inténtalo nuevamente.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Error de red. Verifica tu conexión a internet y prueba nuevamente.';
      } else if (error.message?.includes('CORS')) {
        errorMessage = 'Error de configuración de red. Contacta al administrador.';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Tiempo de espera agotado. La red está lenta, inténtalo nuevamente.';
      }
      
      setAiResponse({
        feedback_id: 'error_fallback',
        feedback_text: errorMessage,
        approved: false,
        source: 'error',
        contract_type: 'text',
        timestamp: new Date().toISOString(),
        error_details: {
          type: error.name,
          message: error.message,
          isMobile: /Mobi|Android/i.test(navigator.userAgent)
        }
      });
    } finally {
      setIsProcessingAI(false);
    }
  };

  // 🚀 MANEJAR ENVÍO DE HIPÓTESIS - Sistema IA EduTechPulse Lab con Scoring
  const handleSubmitHypothesis = async (hypothesis) => {
    // 📊 Usar contenido actual como fuente de verdad (DB sync)
    const currentHypothesis = sessionData?.current_hypothesis?.content || hypothesis;
    
    // Guardar la hipótesis del usuario
    setUserHypothesis(currentHypothesis);
    
    // 💾 GUARDAR EN MONGODB AUTOMÁTICAMENTE
    try {
      console.log('[🔍 DEBUG] SessionData en saveMessage:', sessionData);
      console.log('[🔍 DEBUG] Usando session_id:', sessionData?.session_id);
      console.log('[🔍 DEBUG] Payload completo:', {
        sessionId: sessionData?.session_id,
        labId: labId,
        stepId: 'hipotesis_central',
        content: currentHypothesis,
        eventType: 'hipotesis_definition'
      });
      
      await innovatechService.saveMessage({
        sessionId: sessionData?.session_id,
        labId: labId,
        stepId: 'hipotesis_central',
        content: currentHypothesis,
        eventType: 'hipotesis_definition'
      });
      console.log('💾✅ [MENSAJE GUARDADO] Hipótesis guardada exitosamente en MongoDB');
    } catch (error) {
      console.error('[❌ MONGODB] Error guardando hipótesis:', error);
      
      // Verificar si es error de autenticación
      if (error.message?.includes('Token') || error.message?.includes('401') || error.message?.includes('autenticación')) {
        console.error('[🔐 LabStepper] Error de autenticación detectado. Redirigiendo...');
        navigate('/login');
        return;
      }
    }
    
    // Usar función genérica para validación IA
    await processAIFeedback(currentHypothesis, 'hipotesis_central');
  };

  // 🔄 MANEJAR COMPLETAR CIRCUITO BML
  const handleBMLComplete = (circuitData) => {
    console.log('[🎯 USER ACTION] Circuito BML completado por usuario'); // ✅ Acción usuario
    setBmlCircuitData(circuitData);
    setLabProgress(prev => ({
      ...prev,
      circuito_bml: true
    }));
  };

  // 🤖 SOLICITAR FEEDBACK IA PARA CIRCUITO BML
  const handleBMLAIFeedback = async (circuitData) => {
    console.log('[🤖 USER ACTION] Solicitando análisis IA del circuito BML'); // ✅ Acción usuario
    
    // Generar descripción textual del circuito para enviar a la IA
    const circuitDescription = `
BUILD: MVP: ${circuitData.build?.mvp || 'No especificado'}
Características: ${circuitData.build?.features?.join(', ') || 'No especificado'}
Tiempo estimado: ${circuitData.build?.timeline || 'No especificado'}

MEASURE: Métricas clave: ${circuitData.measure?.metrics?.join(', ') || 'No especificado'}
Criterios de éxito: ${circuitData.measure?.successCriteria?.join(', ') || 'No especificado'}

LEARN: Decisiones basadas en datos: ${circuitData.learn?.decisions?.join(', ') || 'No especificado'}
Próxima hipótesis a probar: ${circuitData.learn?.nextHypothesis || 'No especificado'}
    `.trim();

    // Llamar al sistema de IA usando processAIFeedback existente
    await processAIFeedback(circuitDescription);
  };

  // 📝 MANEJAR COMPLETAR QUIZ
  const handleQuizComplete = (results) => {
    console.log('[🎯 USER ACTION] Quiz completado - Puntuación:', results.score + '%'); // ✅ Acción usuario
    setQuizResults(results);
    
    // 💾 CALCULAR APROBACIÓN: Score ≥ 70 para consistencia total
    const isQuizApproved = results.score >= 70;
    
    // 💾 Actualizar progreso local
    setLabProgress(prev => ({
      ...prev,
      quiz: isQuizApproved
    }));

    // 💾 Persistir en localStorage usando labPersistence
    
    if (labPersistence) {
      console.log(`[💾 PERSISTENCIA] Quiz - Score: ${results.score}%, Aprobado: ${isQuizApproved}`);
      labPersistence.saveStepProgress('quiz', {
        approved: isQuizApproved,
        score: results.score,
        formData: null, // Quiz no tiene form data
        aiResponse: results // Los resultados del quiz van en aiResponse
      });
    }
  };

  // 🎲 MANEJAR COMPLETAR SIMULACIÓN
  const handleSimulationComplete = (simulationResults) => {
    console.log('[🎲 USER ACTION] Simulación completada:', simulationResults);
    
    // 💾 Guardar datos de simulación en persistencia
    if (labPersistence) {
      console.log('[💾 PERSISTENCIA] Guardando resultados de simulación');
      labPersistence.saveStepProgress('bml_simulate', {
        approved: true, // Auto-advance según configuración
        score: null, // No aplica para simulación
        formData: null, // La simulación no tiene form data
        aiResponse: simulationResults // Los datos de la simulación van en aiResponse
      });
    }
    
    // 💾 Actualizar progreso local
    setLabProgress(prev => ({
      ...prev,
      bml_simulate: true
    }));
  };

  // 📊 CALCULAR PROGRESO GENERAL DEL LABORATORIO
  const getLabProgressPercentage = () => {
    // ✅ Usar mismos pasos que useLabPersistence para consistencia
    if (labPersistence && labPersistence.getLabProgressPercentage) {
      return labPersistence.getLabProgressPercentage();
    }
    
    // Fallback si no hay persistencia - usar fases BML individuales
    const steps = ['contexto', 'hipotesis', 'bml_build', 'bml_measure', 'bml_learn', 'quiz'];
    const completedSteps = steps.filter(step => labProgress[step]).length;
    return Math.round((completedSteps / steps.length) * 100);
  };

  // 📊 OBTENER RESULTADOS VALIDADOS DEL QUIZ
  const getValidatedQuizResults = () => {
    console.log('[🔍 DEBUG] getValidatedQuizResults - quizResults:', quizResults);
    
    if (!quizResults) {
      console.log('[⚠️ DEBUG] Quiz results is null/undefined');
      return { score: 0, passed: false, details: null };
    }
    
    const validatedResults = {
      score: quizResults.score || 0,
      passed: quizResults.passed || false,
      correctAnswers: quizResults.correctAnswers || 0,
      totalQuestions: quizResults.totalQuestions || 5,
      details: quizResults.details || null
    };
    
    console.log('[✅ DEBUG] Returning validated results:', validatedResults);
    return validatedResults;
  };

  // 🎯 RENDERIZAR STEP ACTUAL - Arquitectura modular
  const renderCurrentStep = () => {
    const stepComponents = {
      0: <ContextoStep 
           onNext={() => handleStepNavigation('next')}
           labData={{
             title: currentLab.title,
             description: currentLab.description,
             progress: labPersistence.getLabProgressPercentage(),
             currentStep: currentStep + 1,
             totalSteps: currentLab.steps.length
           }}
         />,
      
      1: <HipotesisStep 
           onSubmit={handleSubmitHypothesis}
           isProcessing={isProcessingAI}
           labPersistence={labPersistence}
           sessionData={sessionData}
           userHypothesis={userHypothesis}
           labData={{
             title: currentLab.title,
             description: currentLab.description,
             progress: labPersistence.getLabProgressPercentage(),
             currentStep: currentStep + 1,
             totalSteps: currentLab.steps.length
           }}
         />,
      
      2: <BMLBuildStep
           onValidateIA={processAIFeedback}
           initialData={{
             ...(labPersistence.getStepState('bml_build')?.formData || bmlBuildData),
             hypothesis: sessionData?.current_hypothesis?.content || userHypothesis || "Tu hipótesis aparecerá aquí cuando completes el paso anterior"
           }}
           isActive={true}
         />,
      
      3: <BMLMeasureStep
           onValidateIA={processAIFeedback}
           initialData={labPersistence.getStepState('bml_measure')?.formData || bmlMeasureData}
           isActive={true}
           sessionData={{
             hypothesis: sessionData?.current_hypothesis?.content || userHypothesis || "Tu hipótesis aparecerá aquí cuando completes el paso anterior",
             mvp_summary: labPersistence.getStepState('bml_build')?.formData?.mvp_descripcion || bmlBuildData?.mvp_descripcion || '',
             sessionId: sessionData?.session_id
           }}
         />,
      
      4: <BMLSimulateStep
           onSimulationComplete={handleSimulationComplete}
           onValidateIA={processAIFeedback}
           initialData={{
             ...(labPersistence.getStepState('bml_simulate')?.formData || bmlSimulateData),
             hypothesis: sessionData?.current_hypothesis?.content || userHypothesis || "Tu hipótesis aparecerá aquí cuando completes el paso anterior"
           }}
           measureData={{
             metricas_clave: labPersistence.getStepState('bml_measure')?.formData?.metricas_clave || bmlMeasureData?.metricas_clave || [],
             metodos_medicion: labPersistence.getStepState('bml_measure')?.formData?.metodos_medicion || bmlMeasureData?.metodos_medicion || [],
             objetivos_especificos: labPersistence.getStepState('bml_measure')?.formData?.objetivos_especificos || bmlMeasureData?.objetivos_especificos || '',
             hypothesis: sessionData?.current_hypothesis?.content || userHypothesis || "Tu hipótesis aparecerá aquí cuando completes el paso anterior"
           }}
           isActive={true}
         />,
      
      5: <BMLLearnStep
           onValidateIA={processAIFeedback}
           initialData={labPersistence.getStepState('bml_learn')?.formData || bmlLearnData}
           isActive={true}
         />,
      
      6: <QuizStep
           onComplete={handleQuizComplete}
           minimumScore={70}
           userHypothesis={userHypothesis}
           bmlCircuitData={bmlCircuitData}
           bmlBuildData={bmlBuildData}
           bmlMeasureData={bmlMeasureData}
           bmlLearnData={bmlLearnData}
           simulationResults={bmlSimulateData}
         />,
      
      7: <ResumenStep
           onFinish={() => navigate('/dashboard/innova-tech')}
           userHypothesis={userHypothesis}
           circuitData={bmlCircuitData}
           quizResults={quizResults}
           getLabProgressPercentage={getLabProgressPercentage}
           getValidatedQuizResults={getValidatedQuizResults}
           labPersistence={labPersistence}
         />
    };
    
    return stepComponents[currentStep] || null;
  };

  // ✅ VERIFICAR SI PUEDE AVANZAR AL SIGUIENTE PASO (con validación de scoring)
  const canAdvanceToStep = (stepIndex) => {
    // 🔑 Usar getStepKey consistente para mapear índices
    const targetStepId = getStepKey(stepIndex);
    
    // Contexto siempre disponible
    if (stepIndex === 0) return true;
    
    // 💾 Usar persistencia para determinar si puede avanzar
    if (labPersistence.persistedData) {
      const canAdvance = labPersistence.canAdvanceToStep(targetStepId);
      
      console.log(`[💾 GATING] ¿Puede avanzar a ${targetStepId}? ${canAdvance}`, {
        targetStepId,
        stepIndex,
        persistedProgress: labPersistence.persistedData.labProgress
      });
      
      return canAdvance;
    }
    
    // Fallback: usar el sistema anterior si no hay persistencia
    // Validación específica para avanzar DESDE el step de hipótesis (paso 1 → 2)
    if (stepIndex === 2 && currentStep === 1) {
      // Debe tener hipótesis completada Y aprobada por IA (score ≥70)
      const hypothesisCompleted = labProgress.hipotesis;
      const hypothesisApproved = aiResponse?.approved === true;
      
      return hypothesisCompleted && hypothesisApproved;
    }
    
    // Para otros pasos, verificar requisitos previos estándar
    const previousStepKey = getStepKey(stepIndex - 1);
    return labProgress[previousStepKey];
  };

  // 🧭 NAVEGACIÓN CON GATING PEDAGÓGICO
  const handleStepNavigation = (direction) => {
    if (direction === 'next') {
      const nextStep = currentStep + 1;
      
      if (nextStep < currentLab.steps.length) {
        // Verificar si puede avanzar al siguiente paso
        if (canAdvanceToStep(nextStep)) {
          console.log(`[🎯 USER NAVIGATION] Avanzando a step ${nextStep + 1}: ${currentLab.steps[nextStep].title}`); // ✅ Navegación usuario
          setCurrentStep(nextStep);
          // 🧹 Limpiar feedback de etapa anterior al cambiar de paso
          setAiResponse(null);
        } else {
          // Mostrar mensaje de que debe completar el paso actual
          alert('Debes completar el paso actual antes de continuar');
        }
      } else {
        // Completar laboratorio - verificar si cumple requisitos mínimos
        const finalProgress = getLabProgressPercentage();
        if (finalProgress >= 75) { // Requerir 75% mínimo
          setLabProgress(prev => ({ ...prev, resumen: true }));
          console.log('[🎯 LAB COMPLETED] Redirigiendo al dashboard de InnovaTech');
          navigate('/dashboard/innova-tech');
        } else {
          alert('Debes completar al menos el 75% del laboratorio para finalizarlo');
        }
      }
    } else if (direction === 'prev') {
      if (currentStep > 0) {
        console.log(`[🎯 USER NAVIGATION] Retrocediendo a step ${currentStep}: ${currentLab.steps[currentStep - 1].title}`); // ✅ Navegación usuario
        setCurrentStep(currentStep - 1);
        // 🧹 Limpiar feedback al retroceder también
        setAiResponse(null);
      } else {
        navigate('/dashboard/innova-tech');
      }
    }
  };

  const goToHome = () => navigate('/dashboard/innova-tech');

  // Mostrar loading mientras se valida autenticación o se carga sesión
  if (authLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Si no está autenticado, no mostrar nada (ya se redirige)
  if (!isAuthenticated) {
    return null;
  }

  if (!currentLab) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Laboratorio no encontrado
          </h2>
          <p className="text-gray-600 mb-6">
            El laboratorio {labId} no está disponible.
          </p>
          <button
            onClick={goToHome}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Home className="w-4 h-4 mr-2" />
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pt-2 pb-6 space-y-6 overflow-x-hidden">
      {/* 🎨 EPL Header según UX/UI Policy con variante plain para pasos internos */}
      {currentStep !== 0 && (
        <div className="relative">
          <EPLSectionHeader
            variant="plain"
            phase={getStepKey(currentStep)}
            title={currentLab.title}
            subtitle={currentLab.steps[currentStep]?.title}
            icon={getPhaseIcon(getStepKey(currentStep))}
            progress={labPersistence.getLabProgressPercentage()}
            currentStep={currentStep + 1}
            totalSteps={currentLab.steps.length}
          />
          
          {/* Botón Home flotante - reposicionado para header plain */}
          <button
            onClick={goToHome}
            className="absolute top-4 right-6 p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* 🏠 Botón Home para contexto - Estilo diferente */}
      {currentStep === 0 && (
        <div className="flex justify-end mb-4">
          <button
            onClick={goToHome}
            className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
            title="Volver al dashboard"
          >
            <Home className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Indicador de autosave flotante */}
      {showAutosaveIndicator && (
        <div className="fixed top-20 right-6 z-50 flex items-center space-x-2 text-xs text-green-600 bg-green-50 border border-green-200 px-3 py-2 rounded-full shadow-md animate-pulse">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>Guardado automático</span>
        </div>
      )}

      {/* 🎨 EPL Progress Stepper - Sin padding adicional para evitar overflow */}
      {currentStep !== 0 && (
        <div className="pt-2 pb-4">
          <EPLProgressStepper
            variant="transparent"
            steps={currentLab.steps.map((step, index) => {
              const stepKey = getStepKey(index);
              const stepData = labPersistence.getStepState(stepKey);
              return {
                id: step.id,
                title: step.title,
                phase: stepKey,
                completed: stepData?.progress?.approved || (index === 0), // Contexto siempre completado
                active: index === currentStep
              };
            })}
            currentIndex={currentStep}
          />
        </div>
      )}

      {/* 🚀 EduTechPulse Lab - Content Area */}
      <div className="rounded-xl min-h-96">
        <div className="space-y-4">
          {/* 🎯 RENDERIZADO MODULAR - Arquitectura de componentes separados */}
          {renderCurrentStep()}

          {/* Respuesta IA limpia */}
          {isProcessingAI && (
            <AIResponseCard
              aiRole="tutor"
              aiSource="openai"
              isLoading={true}
            />
          )}

          {aiResponse && !isProcessingAI && (
            <AIResponseCard
              // 🎯 CONTRATO JSON v1.0: Manejo correcto de datos estructurados
              response={aiResponse.contract_type === 'json' ? undefined : (aiResponse.feedback_text || aiResponse.content)}
              aiRole={aiResponse.role || "tutor"}                       // 🎭 Rol desde dispatcher
              aiSource={aiResponse.aiSource || aiResponse.source || "openai"}  // 🔗 Fuente de la respuesta
              contractType={aiResponse.contract_type || "text"}         // 📋 Tipo de contrato desde JSON
              gating={aiResponse.gating}                                // 🚪 Información de gating pedagógico
              content={aiResponse.content}                              // 📄 Contenido JSON estructurado
              
              // 📊 Compatibilidad con sistema legacy (será removido)
              evaluation={aiResponse.evaluation}
              approved={aiResponse.approved}
            />
          )}
        </div>
      </div>

      {/* Navigation - Adaptativa según el paso */}
      {currentStep > 0 && (
        <div className="flex justify-between">
          {/* Botón Anterior - Visible desde paso 1 en adelante */}
          <button
            onClick={() => handleStepNavigation('prev')}
            className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </button>
        
          {/* Botón Siguiente - Siempre visible en pasos activos */}
          <button
            onClick={() => handleStepNavigation('next')}
            disabled={!canAdvanceToStep(currentStep + 1) && currentStep < currentLab.steps.length - 1}
            className={`inline-flex items-center px-4 py-2 rounded-lg transition-all ${
              canAdvanceToStep(currentStep + 1) || currentStep === currentLab.steps.length - 1
                ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {currentStep === currentLab.steps.length - 1 ? 'Completar' : 'Siguiente'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LabStepper;